import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Button, DatePicker, Select, Spin } from 'antd';
import { ProPageHeader as PageHeader } from '@ant-design/pro-layout';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import toastr from 'toastr';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getSport, getCompbySport, GetCsvData } from '../../store/slices/adminSlice';

const { RangePicker } = DatePicker;
const { Option } = Select;

// TypeScript interfaces
interface CompItem {
  _id: string;
  competitionname: string;
  isPublic?: boolean;
  isBanter?: boolean;
}

interface CsvDataItem {
  email: string;
  name: string;
  message: string;
  media: string;
  createdAt: string;
}

// Static configurations
const CSV_HEADERS = [
  { label: 'Email', key: 'email' },
  { label: 'Name', key: 'name' },
  { label: 'Message', key: 'message' },
  { label: 'media', key: 'media' },
  { label: 'date/time', key: 'createdAt' },
];

const Banter: React.FC = () => {
  const dispatch = useAppDispatch();

  // Redux state
  const sportData = useAppSelector((state) => state.admin.sportData);

  // Local state
  const [loading, setLoading] = useState(true);
  const [compData, setCompData] = useState<CompItem[]>([]);
  const [selectedComp, setSelectedComp] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>({ start: null, end: null });
  const [buttonLoading, setButtonLoading] = useState(false);
  const [csvData, setCsvData] = useState<CsvDataItem[]>([]);
  const [competitionName, setCompetitionName] = useState<string | null>(null);

  // Fetch sports on mount
  useEffect(() => {
    setLoading(true);
    dispatch(getSport({ page: 0, count: 1000, isActive: true })).finally(() => setLoading(false));
  }, [dispatch]);

  // Handlers
  const handleSportChange = useCallback(async (sportId: string) => {
    setLoading(true);
    try {
      const action = await dispatch(getCompbySport({ sportId, count: 1000 }));
      const data = (action.payload as CompItem[]) || [];
      const banterComps = data.filter((comp) => comp.isPublic === false && comp.isBanter === true);
      setCompData(banterComps);
    } catch {
      setCompData([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  const handleDateChange = useCallback((value: any) => {
    if (value?.length === 2) {
      setDateRange({ start: value[0]._d, end: value[1]._d });
    }
  }, []);

  const handleFilter = useCallback(async () => {
    if (!selectedComp || !dateRange.start || !dateRange.end) {
      toastr.warning('Please select all fields');
      return;
    }

    setButtonLoading(true);
    try {
      const action = await dispatch(GetCsvData({
        compId: selectedComp,
        startDate: dateRange.start,
        endDate: dateRange.end,
      }));

      const data = action.payload as { data?: CsvDataItem[]; competitionName?: string } | undefined;
      if (!data?.data?.length) {
        toastr.info('There are no messages in this banter');
        setCsvData([]);
      } else {
        const formattedData = data.data.map((item) => ({
          ...item,
          createdAt: moment(item.createdAt, 'YYYY-MM-DD HH:mm:ss').format('MMMM DD, YYYY h:mm A'),
        }));
        setCsvData(formattedData);
        setCompetitionName(data.competitionName || null);
      }
    } catch {
      toastr.error('Failed to fetch data');
    } finally {
      setButtonLoading(false);
    }
  }, [dispatch, selectedComp, dateRange]);

  // Memoized options
  const sportOptions = useMemo(() => (
    sportData.map((sport: any, index) => (
      <Option key={index} value={sport._id}>{sport.sportname || sport.name}</Option>
    ))
  ), [sportData]);

  const compOptions = useMemo(() => (
    compData.map((comp, index) => (
      <Option key={index} value={comp._id}>{comp.competitionname}</Option>
    ))
  ), [compData]);

  return (
    <div>
      <Spin spinning={loading} delay={500}>
        <PageHeader prefixedClassName="ant-page-header" title="Banter" subTitle="" />
        <div className="bunter">
          <div className="bunter-filter">
            <Select
              placeholder="Select sport"
              style={{ width: 150 }}
              onChange={handleSportChange}
            >
              <Option value="" disabled>Select sport</Option>
              {sportOptions}
            </Select>

            <Select
              placeholder="Select competition"
              style={{ width: 150 }}
              onChange={(value: string) => setSelectedComp(value)}
            >
              <Option value="" disabled>Select competition</Option>
              {compOptions}
            </Select>

            <RangePicker
              onChange={handleDateChange}
              placeholder={['Start date', 'End date']}
            />

            <Button
              type="primary"
              loading={buttonLoading}
              onClick={handleFilter}
            >
              Filter
            </Button>

            {csvData.length > 0 && (
              <CSVLink
                filename={`${competitionName}.csv`}
                className="btn btn-primary"
                target="_blank"
                data={csvData}
                headers={CSV_HEADERS}
              >
                Download {competitionName}
              </CSVLink>
            )}
          </div>
        </div>
      </Spin>
    </div>
  );
};

export default Banter;



